import React, { useMemo } from 'react';
import { useExtensionStore } from './store';
import { effectiveGrantedCapabilities } from './capabilities/enforce';
import ExtensionHost from './ExtensionHost';
import ExtensionSidebarSections from './ExtensionSidebarSections';
import { usePreferencesStore } from '../store/preferencesStore';
import { idleTeardownMsFor } from './dbIdleDisconnect';

/**
 * Persistently-mounted overlay covering AppContent's sidebar column only (not
 * ActivityBar, not the main tab strip/editor pane), rendered as a sibling of
 * <AppContent> from App.js. This exists outside AppContent's own teams/admin/
 * default branching on purpose: that branching fully unmounts whichever view
 * isn't active, which is fine for Teams/Admin (cheap to remount) but would
 * defeat the whole freeze-then-idle-teardown design for extension panels — an
 * extension must stay mounted-but-hidden for its grace period after a tab
 * switch, not be unmounted immediately. AppContent.js itself is untouched by
 * this.
 *
 * Each activated panel swaps in its own Collections-styled sidebar column
 * (containing only that panel's own contributed sidebar section), same as
 * before. Unlike before, the main content area is deliberately left
 * pointer-events:none and paints nothing over it — AppContent's `children`
 * (the shared EnhancedTabManager + EditorPane, always mounted underneath
 * regardless of activeTab) shows through untouched, so browsing an
 * extension's tree behaves like Collections: the tab strip stays visible and
 * switchable while the sidebar swaps. Table/result tabs it opens (e.g. DB
 * Manager's `sdk.ui.openTable`) render as ordinary tabs in that shared strip,
 * not inside this layer.
 *
 * The guest iframe (ExtensionHost) still needs to stay mounted while its
 * panel is active/idle-grace so its RPC + tree-publishing lifecycle keeps
 * running, but no longer needs any visible surface now that panel UI
 * (results/schema grids) is host-rendered as shared tabs — it's kept mounted
 * at a near-zero, non-interactive footprint instead.
 *
 * Renders nothing (zero DOM footprint) until at least one extension panel
 * has ever been activated; each activated panel then keeps its own wrapper
 * mounted (CSS `display:none` when not the active tab) until its own
 * ExtensionHost calls back to deactivate it.
 */
export default function ExtensionHostLayer({ activeTab, sidebarWidth, workspaceId }) {
  const activatedPanels = useExtensionStore((s) => s.activatedPanels);
  const extensions = useExtensionStore((s) => s.extensions);
  const entrySources = useExtensionStore((s) => s.entrySources);
  const grantedCapabilities = useExtensionStore((s) => s.grantedCapabilities);
  const deactivatePanel = useExtensionStore((s) => s.deactivatePanel);
  // Settings → Extensions → DB Manager: how long a hidden DB Manager keeps its connections.
  const dbIdleDisconnect = usePreferencesStore((s) => s.dbIdleDisconnect);

  const panelToExtension = useMemo(() => {
    const idx = new Map();
    for (const manifest of Object.values(extensions)) {
      for (const panel of manifest.contributes?.panels || []) {
        idx.set(panel.id, manifest.id);
      }
    }
    return idx;
  }, [extensions]);

  // A panel only gets the sidebar-shaped overlay column if its extension
  // actually contributes a sidebarSection for it — a full-page panel (e.g.
  // Task Manager) has none, and painting an empty background column over
  // AppContent's full-page view for it would just black out its own UI.
  const panelHasSidebar = useMemo(() => {
    const idx = new Map();
    for (const manifest of Object.values(extensions)) {
      for (const panel of manifest.contributes?.panels || []) {
        const has = (manifest.contributes?.sidebarSections || []).some(
          (section) => section.activityBarId === panel.activityBarId
        );
        idx.set(panel.id, has);
      }
    }
    return idx;
  }, [extensions]);

  // Effective grant set per extension (see the note at the ExtensionHost call
  // below), memoised so the prop keeps a stable identity — ExtensionHost pushes
  // it into the live bridge on every change of it. Recomputing when the store's
  // grant map changes is what keeps a revoke in Settings effective on the guest
  // without a remount; the subscription above is the reason this re-runs at all.
  const grantsFor = useMemo(() => {
    const byId = new Map(
      Object.keys(extensions).map((id) => [id, effectiveGrantedCapabilities(id)])
    );
    return (extensionId) => byId.get(extensionId) || grantedCapabilities[extensionId] || [];
  }, [extensions, grantedCapabilities]);

  const panelIds = Object.keys(activatedPanels);
  if (panelIds.length === 0) return null;

  return (
    <div className="absolute inset-0 z-10" style={{ pointerEvents: 'none' }}>
      {panelIds.map((panelId) => {
        const extensionId = panelToExtension.get(panelId);
        const entrySource = extensionId ? entrySources[extensionId] : null;
        if (!extensionId || !entrySource) return null;
        const isVisible = activeTab === panelId;
        return (
          <div key={panelId} style={{ display: isVisible ? 'block' : 'none' }}>
            {panelHasSidebar.get(panelId) && (
              <div
                className="absolute inset-y-0 left-0 h-full overflow-hidden"
                style={{ width: sidebarWidth, pointerEvents: 'auto' }}
              >
                <div className="w-full h-full bg-gray-50 flex flex-col dark:bg-zinc-900">
                  <ExtensionSidebarSections panelId={panelId} workspaceId={workspaceId} />
                </div>
              </div>
            )}
            <div
              className="absolute w-px h-px overflow-hidden opacity-0"
              style={{ pointerEvents: 'none' }}
              aria-hidden="true"
            >
              {/* EFFECTIVE grants, not the persisted array. A compiled-in
                  first-party extension is exempt from the capability gate
                  (capabilities/enforce.js) and its persisted set is empty, so
                  passing the raw array here would hand hostBridge an empty
                  grant set and silently take away the half of DB Manager that
                  still lives in the guest — the schema tree and every
                  connection dialog — plus ExtensionHost's per-engine
                  disconnect-on-teardown backstop, which walks this same list.
                  hostBridge itself is unchanged and still refuses anything
                  outside the set it is given; this decides what the set IS. */}
              <ExtensionHost
                extensionId={extensionId}
                entrySource={entrySource}
                isVisible={isVisible}
                grantedCapabilities={grantsFor(extensionId)}
                idleTeardownMs={idleTeardownMsFor(extensionId, dbIdleDisconnect)}
                onDisposed={() => deactivatePanel(panelId)}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
